import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

import { Check, Star, ArrowLeft } from "lucide-react";
import NavigationMenu from "@/components/layout/NavigationMenu";
import SEO from "@/components/seo/SEO";

const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState<any>(null);
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation('subscription');
  
  useEffect(() => {
    const storedData = localStorage.getItem('pendingRegistration');
    if (storedData) {
      setRegistrationData(JSON.parse(storedData));
    }
  }, []);

  const handleSubscribe = async (planType: 'monthly' | 'annual') => {
    if (!registrationData) {
      toast({
        title: t('common:error', 'Erreur'),
        description: t('pricingPage.errorNoRegistration'),
        variant: "destructive",
      });
      navigate("/auth");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          planType,
          promoCode: promoCode || undefined,
          email: registrationData.email,
          username: registrationData.username,
          firstName: registrationData.firstName,
          lastName: registrationData.lastName,
          residenceCountry: registrationData.residenceCountry,
          isRegistration: true
        }
      });

      if (error) {
        console.error('Checkout error:', error);
        toast({
          title: t('common:error', 'Erreur'),
          description: t('pricingPage.errorCheckout'),
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      if (data?.url) {
        localStorage.removeItem('pendingRegistration');
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error('Error creating checkout:', err);
      toast({
        title: t('common:error', 'Erreur'),
        description: t('pricingPage.errorGeneric'),
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const featuresData = t('features', { returnObjects: true });
  const features = Array.isArray(featuresData) ? featuresData : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <SEO
        title="Tarifs — Golf Progress Calculator"
        description="Découvrez nos abonnements mensuel et annuel à Golf Progress Calculator avec 1 mois d'essai offert pour suivre votre progression au golf."
        path="/pricing"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Golf Progress Calculator",
          description: "Application de suivi de progression au golf — abonnement mensuel ou annuel.",
          offers: [
            { "@type": "Offer", name: "Mensuel", price: "5.00", priceCurrency: "EUR" },
            { "@type": "Offer", name: "Annuel", price: "50.00", priceCurrency: "EUR" }
          ]
        }}
      />
      <NavigationMenu />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/auth")}
              className="mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('pricingPage.backToRegistration')}
            </Button>
            
            {registrationData && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-green-800">
                  {t('pricingPage.registrationFor')} <strong>{registrationData.email}</strong>
                </p>
              </div>
            )}
            
            <h1 className="text-4xl font-bold mb-4">
              {t('pricingPage.mainTitle')}
            </h1>
            <p className="text-xl text-muted-foreground mb-4">
              {t('pricingPage.mainSubtitle')}
            </p>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-8">
              <p className="text-center text-primary font-semibold">
                {t('pricingPage.freeMonthBanner')}
              </p>
            </div>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className={`${!isAnnual ? 'font-semibold' : 'text-muted-foreground'}`}>
                {t('pricing.monthly')}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAnnual(!isAnnual)}
                className={`relative ${isAnnual ? 'bg-primary text-primary-foreground' : ''}`}
              >
                {isAnnual ? t('pricing.annual') : t('pricing.monthly')}
              </Button>
              <span className={`${isAnnual ? 'font-semibold' : 'text-muted-foreground'}`}>
                {t('pricing.annual')}
              </span>
              {isAnnual && (
                <Badge variant="secondary" className="ml-2">
                  {t('pricing.twoMonthsFree')}
                </Badge>
              )}
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Monthly Plan */}
            <Card className={`relative ${!isAnnual ? 'ring-2 ring-primary shadow-lg scale-105' : ''}`}>
              {!isAnnual && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground">
                    <Star className="h-3 w-3 mr-1" />
                    {t('pricing.popular')}
                  </Badge>
                </div>
              )}
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">{t('pricing.monthlyPlan')}</CardTitle>
                <CardDescription>{t('pricing.monthlyDescription')}</CardDescription>
                <div className="text-4xl font-bold mt-4">
                  {t('pricing.monthlyPrice')}<span className="text-lg font-normal text-muted-foreground">{t('pricing.monthlyPeriod')}</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={() => handleSubscribe('monthly')}
                  disabled={isLoading || !registrationData}
                >
                  {isLoading ? t('pricing.redirecting') : t('pricing.choosePlan')}
                </Button>
              </CardContent>
            </Card>

            {/* Annual Plan */}
            <Card className={`relative ${isAnnual ? 'ring-2 ring-primary shadow-lg scale-105' : ''}`}>
              {isAnnual && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground">
                    <Star className="h-3 w-3 mr-1" />
                    {t('pricing.bestOffer')}
                  </Badge>
                </div>
              )}
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">{t('pricing.annualPlan')}</CardTitle>
                <CardDescription>{t('pricing.annualDescription')}</CardDescription>
                <div className="text-4xl font-bold mt-4">
                  {t('pricing.annualPrice')}<span className="text-lg font-normal text-muted-foreground">{t('pricing.annualPeriod')}</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  <span className="line-through">{t('pricing.originalPrice')}</span> - {t('pricing.savings')}
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={() => {
                    window.location.href = "https://payment.golf-progress-calculator.com/b/8x2bJ1akO5kf8Ii5Qv4gg01";
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? t('pricing.redirecting') : t('pricing.choosePlan')}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Promo Code */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-center">{t('promoCode.title')}</CardTitle>
              <CardDescription className="text-center">
                {t('promoCode.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 max-w-md mx-auto">
                <Input
                  placeholder={t('promoCode.placeholder')}
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* FAQ */}
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">{t('faq.title')}</h2>
            <div className="grid md:grid-cols-2 gap-6 text-left">
              <div>
                <h3 className="font-semibold mb-2">{t('faq.cancelQuestion')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('faq.cancelAnswer')}
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">{t('faq.trialQuestion')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('faq.trialAnswer')}
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">{t('faq.paymentQuestion')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('faq.paymentAnswer')}
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">{t('faq.changePlanQuestion')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('faq.changePlanAnswer')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;